// frontend/src/pages/manufacturing/OrderReconciliation.js

import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  IconButton,
  Collapse,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  PlayArrow as PlayArrowIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MergeType as ConsolidateIcon,
  AccountTree as TreeIcon,
  Factory as FactoryIcon,
  Layers as LayersIcon,
  ShoppingCart as OrderIcon,
  Settings as GearIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';

const OrderReconciliation = () => {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creationProgress, setCreationProgress] = useState({ current: 0, total: 0, results: [] });
  const [expandedRows, setExpandedRows] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await manufacturingAPI.reconciliation.fullValidation();
      setReportData(response.data);

      // AUTO-SELECT ONLY TOP-LEVEL (customer order) GAPS WITH OPERATIONS
      const initialSelection = {};
      let topLevelCount = 0;
      let skippedNoOp = 0;
      
      response.data.actionable_gaps?.forEach((item) => {
        if (item.gap > 0 && item.gap_type === 'customer_order') {
          if (item.has_operation) {
            initialSelection[item.item_id] = true;
            topLevelCount++;
          } else {
            skippedNoOp++;
          }
        }
      });
      setSelectedItems(initialSelection);

      // Show helpful message about the workflow
      const woInputGaps = response.data.wo_input_gaps?.length || 0;
      const deeperGaps = response.data.deeper_bom_gaps?.length || 0;
      const totalSubGaps = woInputGaps + deeperGaps;
      
      if (skippedNoOp > 0) {
        toast.warning(
          `${skippedNoOp} item(s) skipped - missing assembly operation. ${topLevelCount} item(s) selected.`,
          { autoClose: 8000 }
        );
      } else if (topLevelCount > 0 && totalSubGaps > 0) {
        toast.info(
          `${topLevelCount} customer order gap(s) auto-selected. ${totalSubGaps} sub-component gap(s) will be handled via BOM explosion.`,
          { autoClose: 6000 }
        );
      } else if (topLevelCount > 0) {
        toast.success(`Validation complete - ${topLevelCount} gap(s) found`);
      } else if (totalSubGaps > 0) {
        toast.warning(
          `No top-level gaps with operations, but ${totalSubGaps} sub-component gap(s) found.`,
          { autoClose: 6000 }
        );
        // Auto-select sub-component gaps that have operations
        response.data.actionable_gaps?.forEach((item) => {
          if (item.has_operation && item.gap > 0) {
            initialSelection[item.item_id] = true;
          }
        });
        setSelectedItems(initialSelection);
      } else {
        toast.success('Validation complete - no gaps found!');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const selectTopLevelOnly = () => {
    const topLevelOnly = {};
    let skipped = 0;
    
    reportData.actionable_gaps?.forEach((item) => {
      if (item.gap > 0 && item.gap_type === 'customer_order') {
        if (item.has_operation) {
          topLevelOnly[item.item_id] = true;
        } else {
          skipped++;
        }
      }
    });
    setSelectedItems(topLevelOnly);
    
    const count = Object.keys(topLevelOnly).length;
    if (skipped > 0) {
      toast.warning(`Selected ${count} top-level item(s), skipped ${skipped} without operations`);
    } else {
      toast.info(`Selected ${count} top-level item(s)`);
    }
  };

  const selectSubComponentsOnly = () => {
    const subOnly = {};
    let skipped = 0;
    
    reportData.actionable_gaps?.forEach((item) => {
      if (item.gap > 0 && item.gap_type !== 'customer_order') {
        if (item.has_operation) {
          subOnly[item.item_id] = true;
        } else {
          skipped++;
        }
      }
    });
    setSelectedItems(subOnly);
    
    const count = Object.keys(subOnly).length;
    if (skipped > 0) {
      toast.warning(`Selected ${count} sub-component(s), skipped ${skipped} without operations`);
    } else {
      toast.info(`Selected ${count} sub-component(s)`);
    }
  };

  const createAllWorkOrders = async () => {
    const itemsToCreate = reportData.actionable_gaps?.filter(
      (item) => selectedItems[item.item_id] && item.has_operation && item.gap > 0
    ) || [];

    if (itemsToCreate.length === 0) {
      toast.warning('No valid items selected');
      return;
    }

    setCreating(true);
    setCreationProgress({ current: 0, total: itemsToCreate.length, results: [] });

    try {
      // Build batch request - use bom_explosion_qty for correct calculation
      const batchItems = itemsToCreate.map((item) => {
        const primaryOrder = item.customer_orders?.[0];
        return {
          item_id: item.item_id,
          quantity: item.bom_explosion_qty || item.gap,
          customer_order_id: primaryOrder?.order_id || null,
          due_date: item.earliest_due_date,
        };
      });

      console.log('Batch BOM explosion request:', batchItems);

      const response = await manufacturingAPI.reconciliation.batchBomExplosion({ items: batchItems });

      const data = response.data;
      
      setCreationProgress({
        current: itemsToCreate.length,
        total: itemsToCreate.length,
        results: [{
          success: data.success,
          message: data.message,
          created: data.created || 0,
          consolidated: data.consolidated || 0,
          items_processed: data.items_processed || 0,
          items_with_shortage: data.items_with_shortage || 0,
        }],
      });

      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.error || 'Failed to create work orders');
      }

    } catch (error) {
      console.error('Batch BOM explosion error:', error);
      const errorData = error.response?.data;
      
      setCreationProgress({
        current: itemsToCreate.length,
        total: itemsToCreate.length,
        results: [{
          success: false,
          error: errorData?.error || error.message,
          items_missing_operation: errorData?.items_missing_operation || [],
        }],
      });

      toast.error(errorData?.error || error.message);
    }

    setCreating(false);

    // Auto-refresh after creation to show remaining gaps
    setTimeout(() => {
      setShowCreateDialog(false);
      toast.info('Re-running validation to check for remaining gaps...', { autoClose: 3000 });
      fetchReport();
    }, 2500);
  };

  const exportReport = () => {
    if (!reportData) return;
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `validation-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const toggleSelectAll = () => {
    const selectableItems = reportData.actionable_gaps?.filter(
      (item) => item.has_operation && item.gap > 0
    ) || [];
    const allSelected = selectableItems.every((item) => selectedItems[item.item_id]);

    const newSelection = {};
    selectableItems.forEach((item) => {
      newSelection[item.item_id] = !allSelected;
    });
    setSelectedItems(newSelection);
  };

  const toggleRowExpand = (itemId) => {
    setExpandedRows((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const getSelectedCount = () => {
    return Object.values(selectedItems).filter(Boolean).length;
  };

  const getSelectedTopLevelCount = () => {
    return reportData?.actionable_gaps?.filter(
      (item) => selectedItems[item.item_id] && item.gap_type === 'customer_order'
    ).length || 0;
  };

  const getSelectedSubComponentCount = () => {
    return reportData?.actionable_gaps?.filter(
      (item) => selectedItems[item.item_id] && item.gap_type !== 'customer_order'
    ).length || 0;
  };

  const getTopLevelMissingOpCount = () => {
    return reportData?.actionable_gaps?.filter(
      (item) => item.gap_type === 'customer_order' && !item.has_operation && item.gap > 0
    ).length || 0;
  };

  const getSelectableTopLevelCount = () => {
    return reportData?.actionable_gaps?.filter(
      (item) => item.gap_type === 'customer_order' && item.has_operation && item.gap > 0
    ).length || 0;
  };

  const getSelectableSubComponentCount = () => {
    return reportData?.actionable_gaps?.filter(
      (item) => item.gap_type !== 'customer_order' && item.has_operation && item.gap > 0
    ).length || 0;
  };

  const getSubComponentMissingOpCount = () => {
    return reportData?.actionable_gaps?.filter(
      (item) => item.gap_type !== 'customer_order' && !item.has_operation && item.gap > 0
    ).length || 0;
  };

  const getGapTypeIcon = (gapType) => {
    switch (gapType) {
      case 'customer_order':
        return <OrderIcon fontSize="small" color="primary" />;
      case 'work_order_input':
        return <GearIcon fontSize="small" color="secondary" />;
      case 'deeper_bom':
        return <LayersIcon fontSize="small" color="info" />;
      default:
        return <WarningIcon fontSize="small" />;
    }
  };

  const getGapTypeLabel = (gapType) => {
    switch (gapType) {
      case 'customer_order':
        return 'Customer Order';
      case 'work_order_input':
        return 'WO Input';
      case 'deeper_bom':
        return 'Sub-Component';
      default:
        return 'Unknown';
    }
  };

  const getGapTypeColor = (gapType) => {
    switch (gapType) {
      case 'customer_order':
        return 'primary';
      case 'work_order_input':
        return 'secondary';
      case 'deeper_bom':
        return 'info';
      default:
        return 'default';
    }
  };

  // Summary cards
  const renderSummary = () => {
    if (!reportData?.summary) return null;
    const { summary } = reportData;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <AssignmentIcon fontSize="small" />
              <Typography variant="body2">Open Orders</Typography>
              <Typography variant="h4">{summary.total_customer_orders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText', height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <BuildIcon fontSize="small" />
              <Typography variant="body2">Active WOs</Typography>
              <Typography variant="h4">{summary.total_active_work_orders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card
            sx={{
              bgcolor: summary.top_level_gaps > 0 ? 'error.light' : 'success.light',
              color: summary.top_level_gaps > 0 ? 'error.contrastText' : 'success.contrastText',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <OrderIcon fontSize="small" />
              <Typography variant="body2">Order Gaps</Typography>
              <Typography variant="h4">{summary.top_level_gaps}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card
            sx={{
              bgcolor: (summary.wo_input_gaps + summary.deeper_bom_gaps) > 0 ? 'warning.light' : 'success.light',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <LayersIcon fontSize="small" />
              <Typography variant="body2">Sub-Component Gaps</Typography>
              <Typography variant="h4">{summary.wo_input_gaps + summary.deeper_bom_gaps}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: summary.items_missing_operation > 0 ? 'error.light' : 'grey.200', height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <ErrorIcon fontSize="small" />
              <Typography variant="body2">Missing Ops</Typography>
              <Typography variant="h4">{summary.items_missing_operation}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: 'success.light', height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <AddIcon fontSize="small" />
              <Typography variant="body2">WOs Needed</Typography>
              <Typography variant="h4">{summary.total_work_orders_to_create + summary.total_work_orders_to_consolidate}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Gap row component
  const GapRow = ({ item }) => {
    const isExpanded = expandedRows[item.item_id];
    const isSelected = selectedItems[item.item_id];
    const canSelect = item.has_operation && item.gap > 0;
    const isMissingOperation = !item.has_operation && item.gap > 0;

    return (
      <>
        <TableRow
          hover
          sx={{
            bgcolor: isMissingOperation 
              ? 'error.50' 
              : isSelected 
                ? 'primary.50' 
                : 'inherit',
            opacity: isMissingOperation ? 0.85 : 1,
            '& > *': { borderBottom: 'unset' },
          }}
        >
          <TableCell padding="checkbox">
            <Tooltip 
              title={isMissingOperation ? "Cannot select - missing assembly operation" : ""}
              arrow
            >
              <span>
                <Checkbox
                  checked={isSelected || false}
                  onChange={() => toggleItemSelection(item.item_id)}
                  disabled={!canSelect}
                />
              </span>
            </Tooltip>
          </TableCell>
          <TableCell>
            <IconButton size="small" onClick={() => toggleRowExpand(item.item_id)}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>
            <Tooltip title={getGapTypeLabel(item.gap_type)}>
              <Chip
                icon={getGapTypeIcon(item.gap_type)}
                label={getGapTypeLabel(item.gap_type)}
                size="small"
                color={getGapTypeColor(item.gap_type)}
                variant="outlined"
              />
            </Tooltip>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {item.item_code}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.item_name}
                </Typography>
              </Box>
              {isMissingOperation && (
                <Tooltip title="This item needs an assembly operation assigned before work orders can be created">
                  <ErrorIcon color="error" fontSize="small" />
                </Tooltip>
              )}
            </Box>
          </TableCell>
          <TableCell>
            <Chip label={item.item_type.replace('_', ' ')} size="small" variant="outlined" />
          </TableCell>
          <TableCell align="right">
            {item.gap_type === 'customer_order' 
              ? Math.round(item.unallocated_demand || 0)
              : Math.round(item.quantity_needed || 0)
            }
          </TableCell>
          <TableCell align="right">{Math.round(item.stock_available)}</TableCell>
          <TableCell align="right">{Math.round(item.in_production)}</TableCell>
          <TableCell align="right">
            <Typography color="error.main" fontWeight="bold">
              {Math.round(item.gap)}
            </Typography>
          </TableCell>
          <TableCell>
            {item.has_operation ? (
              <Chip label={item.operation_code} size="small" color="success" variant="outlined" />
            ) : (
              <Tooltip title="Assign an assembly operation to this item to create work orders">
                <Chip 
                  icon={<ErrorIcon />}
                  label="NO OPERATION" 
                  size="small" 
                  color="error" 
                />
              </Tooltip>
            )}
          </TableCell>
          <TableCell>
            {item.has_operation && item.wo_analysis && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="New WOs">
                  <Chip
                    icon={<AddIcon />}
                    label={item.wo_analysis.work_orders_to_create}
                    size="small"
                    color="primary"
                  />
                </Tooltip>
                <Tooltip title="Update existing">
                  <Chip
                    icon={<ConsolidateIcon />}
                    label={item.wo_analysis.work_orders_to_consolidate}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Tooltip>
              </Box>
            )}
            {isMissingOperation && (
              <Typography variant="caption" color="error" fontWeight="bold">
                SKIPPED
              </Typography>
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ m: 2 }}>
                {isMissingOperation && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>Missing Assembly Operation</AlertTitle>
                    This item cannot have work orders created until an assembly operation is assigned.
                    <Button 
                      size="small" 
                      href={`/manufacturing/items?search=${item.item_code}`}
                      target="_blank"
                      sx={{ ml: 2 }}
                    >
                      Edit Item
                    </Button>
                  </Alert>
                )}
                <Grid container spacing={2}>
                  {/* Customer Orders (for customer_order type) */}
                  {item.customer_orders?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Customer Orders ({item.customer_orders.length})
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Order</TableCell>
                              <TableCell>Customer</TableCell>
                              <TableCell align="right">Ordered</TableCell>
                              <TableCell align="right">Allocated</TableCell>
                              <TableCell align="right">Need</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {item.customer_orders.slice(0, 5).map((order, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{order.order_number}</TableCell>
                                <TableCell>{order.customer_name}</TableCell>
                                <TableCell align="right">{order.quantity_ordered}</TableCell>
                                <TableCell align="right">{order.quantity_allocated}</TableCell>
                                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                  {Math.round(order.quantity_unallocated)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  )}

                  {/* Source Work Orders (for work_order_input type) */}
                  {item.source_work_orders?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Required By Work Orders ({item.source_work_orders.length})
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>WO #</TableCell>
                              <TableCell>Making</TableCell>
                              <TableCell align="right">Needs</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {item.source_work_orders.slice(0, 5).map((wo, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{wo.wo_number}</TableCell>
                                <TableCell>
                                  <Typography variant="body2">{wo.output_item}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {wo.output_item_name}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{Math.round(wo.quantity_needed)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  )}

                  {/* Parent items (for deeper_bom type) */}
                  {item.parent_items?.length > 0 && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        This is a sub-component of: <strong>{item.parent_items.join(', ')}</strong>
                      </Alert>
                    </Grid>
                  )}

                  {/* Existing Work Orders */}
                  {item.existing_work_orders?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Existing Work Orders ({item.existing_work_orders.length})
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>WO #</TableCell>
                            <TableCell>Op</TableCell>
                            <TableCell>Progress</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {item.existing_work_orders.map((wo, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{wo.wo_number}</TableCell>
                              <TableCell>{wo.operation_code}</TableCell>
                              <TableCell>
                                {wo.quantity_completed}/{wo.quantity_to_produce}
                              </TableCell>
                              <TableCell>
                                <Chip label={wo.status} size="small" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  // Main gaps table
  const renderGapsTable = (gaps, title) => {
    if (!gaps?.length) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h6">No Gaps Found</Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell padding="checkbox" />
              <TableCell width={50} />
              <TableCell>Source</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Required</TableCell>
              <TableCell align="right">Stock</TableCell>
              <TableCell align="right">In Prod</TableCell>
              <TableCell align="right">Gap</TableCell>
              <TableCell>Operation</TableCell>
              <TableCell>WOs Needed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gaps.map((item) => (
              <GapRow key={item.item_id} item={item} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Missing operations alert
  const renderMissingOperations = () => {
    const missingOps = reportData?.items_missing_operation || [];
    if (!missingOps.length) return null;

    // Count how many are top-level vs sub-components
    const topLevelMissing = missingOps.filter(i => i.gap_type === 'customer_order').length;
    const subMissing = missingOps.length - topLevelMissing;

    return (
      <Alert 
        severity="error" 
        sx={{ mb: 3 }}
        action={
          <Button 
            color="inherit" 
            size="small"
            href="/manufacturing/items"
            target="_blank"
          >
            Manage Items
          </Button>
        }
      >
        <AlertTitle>
          {missingOps.length} Items Missing Assembly Operation
          {topLevelMissing > 0 && ` (${topLevelMissing} top-level)`}
        </AlertTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          These items are <strong>skipped</strong> and cannot have work orders created until an assembly operation is assigned:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {missingOps.map((item) => (
            <Tooltip
              key={item.item_id}
              title={`${item.item_name} - Gap: ${Math.round(item.gap)} - ${getGapTypeLabel(item.gap_type)}`}
            >
              <Chip
                icon={getGapTypeIcon(item.gap_type)}
                label={item.item_code}
                color="error"
                variant={item.gap_type === 'customer_order' ? 'filled' : 'outlined'}
                size="small"
              />
            </Tooltip>
          ))}
        </Box>
      </Alert>
    );
  };

  // Workflow guidance
  const renderWorkflowGuidance = () => {
    if (!reportData) return null;
    
    const topLevelGaps = reportData.summary?.top_level_gaps || 0;
    const subGaps = (reportData.summary?.wo_input_gaps || 0) + (reportData.summary?.deeper_bom_gaps || 0);
    const topLevelWithOps = getSelectableTopLevelCount();
    const topLevelWithoutOps = getTopLevelMissingOpCount();
    
    if (topLevelGaps === 0 && subGaps === 0) return null;
    
    if (topLevelWithOps > 0) {
      return (
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ mb: 3 }}
        >
          <AlertTitle>Recommended Workflow</AlertTitle>
          <Typography variant="body2">
            <strong>Step 1:</strong> Create work orders for the <strong>{topLevelWithOps} customer order gap(s)</strong> with operations (auto-selected).
            BOM explosion will automatically create sub-component work orders.
          </Typography>
          {topLevelWithoutOps > 0 && (
            <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>
              <strong>Note:</strong> {topLevelWithoutOps} top-level item(s) are skipped because they're missing assembly operations.
            </Typography>
          )}
          {subGaps > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Step 2:</strong> After creation, the page will refresh. If any of the <strong>{subGaps} sub-component gap(s)</strong> remain, 
              you can create work orders for them in the next pass.
            </Typography>
          )}
        </Alert>
      );
    } else if (topLevelWithoutOps > 0 && topLevelWithOps === 0) {
      return (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
        >
          <AlertTitle>All Top-Level Items Missing Operations</AlertTitle>
          <Typography variant="body2">
            All <strong>{topLevelWithoutOps} customer order gap(s)</strong> are missing assembly operations and cannot be processed.
            Please assign operations to these items first.
          </Typography>
        </Alert>
      );
    } else if (subGaps > 0) {
      return (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
        >
          <AlertTitle>Sub-Component Gaps Only</AlertTitle>
          <Typography variant="body2">
            No customer order gaps found, but there are <strong>{subGaps} sub-component gap(s)</strong>.
            These are inputs needed by existing work orders. Select and create work orders for them.
          </Typography>
        </Alert>
      );
    }
    
    return null;
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Production Validation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Identifies gaps between customer orders and production capacity
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={!reportData}
            onClick={exportReport}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={fetchReport}
            disabled={loading}
          >
            {loading ? 'Validating...' : 'Run Validation'}
          </Button>
        </Box>
      </Box>

      {/* Initial state */}
      {!reportData && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TreeIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Ready to Validate
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
            This tool checks customer orders and existing work orders to find production gaps.
            It will identify what needs to be manufactured and create work orders automatically.
          </Typography>
          <Button variant="contained" size="large" startIcon={<PlayArrowIcon />} onClick={fetchReport}>
            Run Full Validation
          </Button>
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6">Validating...</Typography>
        </Paper>
      )}

      {/* Report content */}
      {reportData && !loading && (
        <>
          {renderSummary()}
          
          {renderWorkflowGuidance()}

          {reportData.summary?.total_actionable_gaps === 0 && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>All Requirements Covered</AlertTitle>
              All customer orders and work orders have sufficient stock or active work orders.
            </Alert>
          )}

          {renderMissingOperations()}

          {/* Action bar */}
          {reportData.actionable_gaps?.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          reportData.actionable_gaps.filter((i) => i.has_operation && i.gap > 0).length > 0 &&
                          reportData.actionable_gaps
                            .filter((i) => i.has_operation && i.gap > 0)
                            .every((item) => selectedItems[item.item_id])
                        }
                        indeterminate={
                          getSelectedCount() > 0 &&
                          getSelectedCount() < reportData.actionable_gaps.filter((i) => i.has_operation && i.gap > 0).length
                        }
                        onChange={toggleSelectAll}
                      />
                    }
                    label="Select All"
                  />
                  
                  <Divider orientation="vertical" flexItem />
                  
                  <Typography variant="body2" color="text.secondary">
                    {getSelectedCount()} selected
                    {getSelectedCount() > 0 && (
                      <>
                        {' '}({getSelectedTopLevelCount()} top-level, {getSelectedSubComponentCount()} sub-components)
                      </>
                    )}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OrderIcon />}
                    onClick={selectTopLevelOnly}
                    disabled={getSelectableTopLevelCount() === 0}
                  >
                    Top-Level ({getSelectableTopLevelCount()})
                    {getTopLevelMissingOpCount() > 0 && (
                      <Chip 
                        label={`${getTopLevelMissingOpCount()} no op`} 
                        size="small" 
                        color="error" 
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LayersIcon />}
                    onClick={selectSubComponentsOnly}
                    disabled={getSelectableSubComponentCount() === 0}
                  >
                    Sub-Components ({getSelectableSubComponentCount()})
                    {getSubComponentMissingOpCount() > 0 && (
                      <Chip 
                        label={`${getSubComponentMissingOpCount()} no op`} 
                        size="small" 
                        color="error" 
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Button>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<FactoryIcon />}
                    disabled={getSelectedCount() === 0}
                    onClick={() => setShowCreateDialog(true)}
                  >
                    Create Work Orders ({getSelectedCount()})
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Tabs for different gap types */}
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>All Gaps ({reportData.actionable_gaps?.length || 0})</span>
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OrderIcon fontSize="small" />
                    <span>Customer Orders ({reportData.top_level_gaps?.length || 0})</span>
                    {getTopLevelMissingOpCount() > 0 && (
                      <Chip label={`${getTopLevelMissingOpCount()} no op`} size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GearIcon fontSize="small" />
                    <span>WO Inputs ({reportData.wo_input_gaps?.length || 0})</span>
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LayersIcon fontSize="small" />
                    <span>Sub-Components ({reportData.deeper_bom_gaps?.length || 0})</span>
                  </Box>
                }
              />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {activeTab === 0 && renderGapsTable(reportData.actionable_gaps, 'All Gaps')}
              {activeTab === 1 && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }} icon={<OrderIcon />}>
                    <strong>Start here!</strong> Products from customer orders that need work orders.
                    BOM explosion will automatically create sub-component work orders.
                    {getTopLevelMissingOpCount() > 0 && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>
                        {getTopLevelMissingOpCount()} item(s) highlighted in red are missing operations and will be skipped.
                      </Typography>
                    )}
                  </Alert>
                  {renderGapsTable(reportData.top_level_gaps, 'Customer Order Gaps')}
                </>
              )}
              {activeTab === 2 && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Inputs needed by existing work orders that don't have their own WOs.
                    (e.g., Table WO exists but Legs WO is missing)
                  </Alert>
                  {renderGapsTable(reportData.wo_input_gaps, 'WO Input Gaps')}
                </>
              )}
              {activeTab === 3 && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Deeper BOM components needed by WO input items.
                  </Alert>
                  {renderGapsTable(reportData.deeper_bom_gaps, 'Deeper BOM Gaps')}
                </>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Create dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => !creating && setShowCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Work Orders</DialogTitle>
        <DialogContent>
          {!creating && creationProgress.results.length === 0 && (
            <>
              {getSelectedTopLevelCount() > 0 && getSelectedSubComponentCount() > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Mixed Selection</AlertTitle>
                  You have selected both top-level items and sub-components. 
                  This may create duplicate work orders if the sub-components are part of the top-level items' BOMs.
                  <br /><br />
                  <strong>Recommendation:</strong> Process top-level items first, then re-run validation.
                </Alert>
              )}
              
              {getSelectedTopLevelCount() > 0 && getSelectedSubComponentCount() === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  BOM explosion will run for each selected item. Sub-component work orders will be created automatically
                  based on the bill of materials. Existing work orders will have quantities consolidated (added together).
                </Alert>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Selected items ({getSelectedCount()}):
              </Typography>
              <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                {reportData?.actionable_gaps
                  ?.filter((item) => selectedItems[item.item_id])
                  .map((item) => (
                    <ListItem
                      key={item.item_id}
                      sx={{ 
                        bgcolor: item.gap_type === 'customer_order' ? 'primary.50' : 'grey.50', 
                        mb: 0.5, 
                        borderRadius: 1 
                      }}
                    >
                      <ListItemIcon>
                        {getGapTypeIcon(item.gap_type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="bold">
                            {item.item_code} - {item.item_name}
                          </Typography>
                        }
                        secondary={
                          <>
                            {getGapTypeLabel(item.gap_type)} | Gap: {Math.round(item.gap)} |
                            WOs: +{item.wo_analysis?.work_orders_to_create || 0} new,
                            ~{item.wo_analysis?.work_orders_to_consolidate || 0} update
                          </>
                        }
                      />
                    </ListItem>
                  ))}
              </List>
            </>
          )}

          {creating && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="h6">Creating Work Orders...</Typography>
              <Typography variant="body2" color="text.secondary">
                Processing BOM explosion for {creationProgress.total} item(s)
              </Typography>
              <LinearProgress sx={{ mt: 2 }} />
            </Box>
          )}

          {!creating && creationProgress.results.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Results
              </Typography>
              <List dense>
                {creationProgress.results.map((result, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      bgcolor: result.success ? 'success.50' : 'error.50',
                      mb: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <ListItemIcon>
                      {result.success ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={result.success ? result.message : 'Error'}
                      secondary={
                        result.success 
                          ? `Created: ${result.created}, Consolidated: ${result.consolidated}`
                          : result.error
                      }
                    />
                  </ListItem>
                ))}
              </List>
              
              {creationProgress.results.some(r => r.success) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  The page will refresh automatically to show any remaining gaps...
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)} disabled={creating}>
            {creationProgress.results.length > 0 ? 'Close' : 'Cancel'}
          </Button>
          {creationProgress.results.length === 0 && (
            <Button
              variant="contained"
              onClick={createAllWorkOrders}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={20} /> : <FactoryIcon />}
            >
              {creating ? 'Creating...' : 'Create Work Orders'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderReconciliation;